import { IconButton, Typography } from '@mui/material';
import { headCellsDocumentMarriage } from 'civil-status/constants/head-cells';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { DocumentSearchMarriageCrumbs } from 'civil-status/constants/breadcrumb';
import {
  DocumentMarriageFilterForm,
  DocumentMarriageVM,
} from 'civil-status/view-models/document-marriage-list';
import FilterDocument from 'civil-status/components/document-marriage-list';
import * as yup from 'yup';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { regexOnlyNumber } from 'shell/constants/regex';
import { Visibility } from '@mui/icons-material';
import { SearchDocument } from 'shell/models/search-document';
import { initialValuesDocumentMarriageSearch } from '..';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { SelectOperatorChoice } from 'shell/constants/select-choice-operator';

export default function DocumentMarriageSearchPage(): React.ReactElement {
  const { userProfile } = useUserProfile();

  const [birthlist, getBirthlist] = useHttpFetch<
    HttpResponseList<SearchDocument>,
    Array<DocumentMarriageVM>
  >(`${__CIVIL_STATUS_API_URL__}/sima/ricerca-atto`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response) => {
      return (
        response.data?.payload?.list?.map((item) => {
          const itemResult: DocumentMarriageVM = {
            code: item.faldoneId || '',
            registerNumberWife: item.cittadinoId || '',
            surnameWife: item.cittadinoCognome || '',
            nameWife: item.cittadinoNome || '',
            registerNumberHusband: item.cittadino2Id || '',
            surnameHusband: item.cittadino2Cognome || '',
            nameHusband: item.cittadino2Nome || '',
            year: item.registroAnno || '',
            documentNumber: item.attoNumero || '',
            user: item.userLock || '',
            locked: !item.userLock
              ? false
              : item.userLock !== userProfile.datiLogin?.key?.usernameEsteso,
            numReg: item.registroNumero || '',
            parte: item.registroParte || '',
            serie: item.registroSerie || '',
          };

          return itemResult;
        }) || []
      );
    },
  });

  const data = birthlist.data as Array<DocumentMarriageVM>;

  const formikForm = useFormik<DocumentMarriageFilterForm>({
    initialValues: initialValuesDocumentMarriageSearch,
    validationSchema: yup
      .object()
      .shape({
        year: yup
          .string()
          .matches(regexOnlyNumber, 'Campo numerico')
          .required('Campo obbligatorio'),
        matricolaHusband: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
        matricolaWife: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
        registry: yup.object().nullable(),
        document: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
        surnameHusband: yup.string(),
        surnameWife: yup.string(),
        nameHusband: yup.string(),
        nameWife: yup.string(),
      })
      .test('', (values) => {
        const valuesCasted = values as unknown as DocumentMarriageFilterForm;
        if (
          valuesCasted.surnameHusband?.includes("'") &&
          valuesCasted.operationSurnameHusband !== SelectOperatorChoice.Contains
        ) {
          return new yup.ValidationError(
            'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
            undefined,
            'surnameHusband'
          );
        }

        if (
          valuesCasted.nameHusband?.includes("'") &&
          valuesCasted.operationNameHusband !== SelectOperatorChoice.Contains
        ) {
          return new yup.ValidationError(
            'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
            undefined,
            'nameHusband'
          );
        }

        if (
          valuesCasted.surnameWife?.includes("'") &&
          valuesCasted.operationSurnameWife !== SelectOperatorChoice.Contains
        ) {
          return new yup.ValidationError(
            'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
            undefined,
            'surnameWife'
          );
        }

        if (
          valuesCasted.nameWife?.includes("'") &&
          valuesCasted.operationNameWife !== SelectOperatorChoice.Contains
        ) {
          return new yup.ValidationError(
            'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
            undefined,
            'nameWife'
          );
        }

        return true;
      }),
    onSubmit: (values) => {
      getBirthlist({
        data: {
          cittadino2Cognome: values.surnameHusband || undefined,
          cittadino2CognomeSearchType: values.surnameHusband
            ? values.operationSurnameHusband
            : undefined,
          cittadino2Id: values.matricolaHusband || undefined,
          cittadino2Nome: values.nameHusband || undefined,
          cittadino2NomeSearchType: values.nameHusband ? values.operationNameHusband : undefined,
          attoNumeroA: values.toDocument || undefined,
          attoNumeroDa: values.document || values.fromDocument || undefined,
          cittadinoCognome: values.surnameWife || undefined,
          cittadinoCognomeSearchType: values.surnameWife ? values.operationSurnameWife : undefined,
          cittadinoId: values.matricolaWife || undefined,
          cittadinoNome: values.nameWife || undefined,
          cittadinoNomeSearchType: values.nameWife ? values.operationNameWife : undefined,
          registroAnno: values.year || undefined,
          registroNumeroA: values.toRegistry?.id || undefined,
          registroNumeroDa: values.registry?.id || values.fromRegistry?.id || undefined,
          registroParte: values.portion || undefined,
          registroSerie: values.serie || undefined,
          tipoAtto: '1', // Matrimonio
        },
      });
    },
  });

  // HTTP Request to get list
  const navigate = useNavigate();

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const filterTable = useFilterTable<DocumentMarriageFilterForm>();

  useEffect(() => {
    formikForm.setValues(filterTable.values || initialValuesDocumentMarriageSearch);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(formikForm.values);
  }, [formikForm.values]);

  const lockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/blocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentMarriageVM> => {
      return new Promise((resolve) => {
        (
          lockDocument({
            data: {
              key: {
                anno: cell.year || '',
                numeroAtto: cell.documentNumber || '',
                numeroRegistro: cell.numReg || '',
                parte: cell.parte || '',
                serie1: cell.serie || '',
              },
            },
          }) as ResponsePromise<void>
        ).then(() => {
          navigate({
            pathname: generatePath(
              '/stato-civile/matrimonio/ricerca/dettaglio/:anno/:numeroRegistro/:numeroAtto/:parte/:serie1',
              {
                anno: cell.year || '',
                numeroAtto: cell.documentNumber || '',
                numeroRegistro: cell.numReg || '',
                parte: cell.parte || '',
                serie1: cell.serie === ' ' ? 'null' : cell.serie,
              }
            ),
          });
          resolve(cell);
        });
      });
    },
    [navigate, lockDocument]
  );

  /**
   * The list of document act.
   */
  const list = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <PmiTooltipError
            show={item.locked}
            message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
          >
            <span>
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onVisibilityPress(item);
                }}
                disabled={item.locked}
              >
                <Visibility />
              </IconButton>
            </span>
          </PmiTooltipError>
        ),
      })) || []
    );
  }, [data, onVisibilityPress]);

  return (
    <>
      <Breadcrumbs items={DocumentSearchMarriageCrumbs} base="stato-civile" />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Ricerca Atti Matrimonio - Gestione ed Emissione
      </Typography>
      <div className="pmi-my-3">
        <FilterDocument filterDocumentForm={formikForm} />
      </div>
      {data ? (
        list?.length > 0 ? (
          <Table
            ariaLabel="Lista atti matrimonio"
            page={formikForm.values.page}
            rowsPerPage={formikForm.values.rowsPerPage}
            body={list}
            count={list.length || 0}
            head={headCellsDocumentMarriage}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            orders={formikForm.values.orders}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </>
  );
}

DocumentMarriageSearchPage.displayName = 'home page component';
